/**
 * Pricing Page - Subscription Plans
 *
 * Modern pricing page with optimized tiers for marketing.
 * Features:
 * - 5 tiers: Free, Starter, Pro (popular), Expert, Enterprise
 * - Annual discount (2 months free)
 * - Feature comparison table
 * - Glassmorphism design
 */

import { useState } from 'react';
import { Icons } from '../components/Icons';
import { useIsMobile } from '../hooks/useIsMobile';
import { useToast } from '../components/Toast';
import { useAuthStore } from '../store/authStore';

type BillingPeriod = 'monthly' | 'annual';

interface PricingTier {
  id: string;
  name: string;
  subtitle: string;
  monthlyPrice: number;
  annualPrice: number;
  currency: string;
  features: string[];
  highlighted?: boolean;
  badge?: string;
  color: string;
  gradient: string;
  icon: keyof typeof Icons;
  comingSoon?: boolean;
}

const PRICING_TIERS: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    subtitle: 'Pour les investisseurs actifs',
    monthlyPrice: 4.99,
    annualPrice: 49,
    currency: '€',
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    icon: 'TrendingUp',
    features: [
      'Portfolio Off-Chain illimité',
      'CoinGecko Pro (temps réel)',
      'Synchronisation cloud',
      'Alertes de prix Light',
      'Historique 1 an',
      'Support email',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    subtitle: 'La solution complète',
    monthlyPrice: 9.99,
    annualPrice: 99,
    currency: '€',
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    icon: 'Zap',
    highlighted: true,
    badge: 'Populaire',
    features: [
      'Tout de Starter +',
      'Portfolio On-Chain (DeBank)',
      'Wallet Tracker multi-wallets (alertes limitées)',
      'Suivi des swaps complet',
      'Performance vs BTC/ETH',
      'Alertes avancées (illimitées)',
      'Historique complet',
      'Support prioritaire',
    ],
  },
  {
    id: 'expert',
    name: 'Baby Whale',
    subtitle: 'Pour les traders sérieux',
    monthlyPrice: 19.99,
    annualPrice: 199,
    currency: '€',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    icon: 'Target',
    features: [
      'Tout de Pro +',
      'Wallet Tracker avancé',
      'Tax Center complet',
      'Tax Loss Harvesting',
      'Analyses AI personnalisées',
      'Opportunités selon profil',
      'Rapports fiscaux exportables',
      'Accès API',
      'Consensus AI (beta)',
    ],
  },
  {
    id: 'enterprise',
    name: 'Whale',
    subtitle: 'Solutions sur mesure',
    monthlyPrice: 49.99,
    annualPrice: 499,
    currency: '€',
    color: '#ef4444',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    icon: 'Globe',
    comingSoon: true,
    features: [
      'Accès à Pro',
      'Multi-comptes & équipes',
      'Bot de trading AI',
      'White-label disponible',
      'Intégrations personnalisées',
      'Support dédié 24/7',
      'SLA garanti 99.9%',
      'Formation incluse',
    ],
  },
];

// Free tier separate for bottom banner
const FREE_TIER: PricingTier = {
  id: 'free',
  name: 'Découverte',
  subtitle: 'Toutes les fonctionnalités essentielles, sans engagement',
  monthlyPrice: 0,
  annualPrice: 0,
  currency: '€',
  color: '#10b981',
  gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  icon: 'Sparkles',
  features: [
    'Portfolio Off-Chain complet',
    'Graphiques & analyses',
    'Prix CoinGecko',
    'Monitoring Nodes & Masternodes',
    'Stockage local sécurisé',
  ],
};

// VIP tiers for hosting/staking customers
interface VIPTier {
  id: string;
  name: string;
  subtitle: string;
  condition: string;
  equivalentPlan: string;
  color: string;
  gradient: string;
  icon: keyof typeof Icons;
  features: string[];
}

const VIP_TIERS: VIPTier[] = [
  {
    id: 'vip',
    name: 'VIP',
    subtitle: 'Réservé à nos clients infrastructure',
    condition: 'Staking 50k CRO OU hébergement nodes ≥ 50€/mois',
    equivalentPlan: 'Pro',
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    icon: 'Star',
    features: [
      'Toutes les fonctionnalités Pro incluses',
      'Dashboard monitoring prioritaire',
      'Alertes nodes en temps réel',
      'Support technique dédié',
      'Accès bêta nouvelles fonctionnalités',
    ],
  },
  {
    id: 'vip-plus',
    name: 'VIP+',
    subtitle: 'Pour nos plus grands partenaires',
    condition: 'Staking 100k CRO OU hébergement nodes ≥ 150€/mois',
    equivalentPlan: 'Baby Whale',
    color: '#ec4899',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
    icon: 'Crown',
    features: [
      'Toutes les fonctionnalités Baby Whale incluses',
      'API dédiée haute disponibilité',
      'Rapports personnalisés mensuels',
      'Ligne directe support 24/7',
      'Consulting stratégie crypto inclus',
      'Invitation événements privés',
    ],
  },
];

// Feature comparison data
const FEATURE_COMPARISON = [
  {
    category: 'Portfolio',
    features: [
      { name: 'Cryptos Off-Chain', free: '10', starter: 'Illimité', pro: 'Illimité', expert: 'Illimité', enterprise: 'Illimité' },
      { name: 'Portfolio On-Chain', free: false, starter: false, pro: true, expert: true, enterprise: true },
      { name: 'Wallets multi-chain', free: false, starter: false, pro: '5', expert: '20', enterprise: 'Illimité' },
      { name: 'Synchronisation cloud', free: false, starter: true, pro: true, expert: true, enterprise: true },
    ],
  },
  {
    category: 'Données & Analytics',
    features: [
      { name: 'Prix temps réel', free: 'Délai 5 min', starter: true, pro: true, expert: true, enterprise: true },
      { name: 'Historique', free: '30 jours', starter: '1 an', pro: 'Complet', expert: 'Complet', enterprise: 'Complet' },
      { name: 'Performance vs BTC', free: false, starter: false, pro: true, expert: true, enterprise: true },
      { name: 'Analyses AI', free: false, starter: false, pro: false, expert: true, enterprise: true },
    ],
  },
  {
    category: 'Alertes & Notifications',
    features: [
      { name: 'Alertes de prix', free: false, starter: '5', pro: 'Illimité', expert: 'Illimité', enterprise: 'Illimité' },
      { name: 'Alertes on-chain', free: false, starter: false, pro: true, expert: true, enterprise: true },
      { name: 'Notifications push', free: false, starter: true, pro: true, expert: true, enterprise: true },
    ],
  },
  {
    category: 'Fiscalité',
    features: [
      { name: 'Tax Center', free: false, starter: false, pro: false, expert: true, enterprise: true },
      { name: 'Tax Loss Harvesting', free: false, starter: false, pro: false, expert: true, enterprise: true },
      { name: 'Rapports fiscaux', free: false, starter: false, pro: false, expert: true, enterprise: true },
    ],
  },
  {
    category: 'Support',
    features: [
      { name: 'Communauté Discord', free: true, starter: true, pro: true, expert: true, enterprise: true },
      { name: 'Support email', free: false, starter: true, pro: true, expert: true, enterprise: true },
      { name: 'Support prioritaire', free: false, starter: false, pro: true, expert: true, enterprise: true },
      { name: 'Support dédié', free: false, starter: false, pro: false, expert: false, enterprise: true },
    ],
  },
];

export function Pricing() {
  const toast = useToast();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annual');
  const [showComparison, setShowComparison] = useState(false);
  const isMobile = useIsMobile(768);
  const { isAuthenticated } = useAuthStore();

  const formatPrice = (tier: PricingTier): string => {
    const price = billingPeriod === 'monthly' ? tier.monthlyPrice : tier.annualPrice / 12;
    if (price === 0) return 'Gratuit';
    return `${price.toFixed(2).replace('.', ',')}${tier.currency}`;
  };

  const getAnnualSavings = (tier: PricingTier): number => {
    if (tier.monthlyPrice === 0) return 0;
    const monthlyCost = tier.monthlyPrice * 12;
    const savings = ((monthlyCost - tier.annualPrice) / monthlyCost) * 100;
    return Math.round(savings);
  };

  const handleSelectPlan = (tierId: string) => {
    // Stripe checkout integration is planned for Phase 6 of the roadmap.
    // For now, we capture interest and route the user to a private beta waitlist.
    console.log('Selected plan:', tierId, billingPeriod);
    toast(
      `Beta privée — early access bientôt. Plan choisi : ${tierId}. Tu seras prévenu.e dès l'ouverture.`,
      'info'
    );
  };

  const styles = {
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: isMobile ? '20px 16px' : '40px 24px',
    } as React.CSSProperties,

    header: {
      textAlign: 'center' as const,
      marginBottom: '48px',
    } as React.CSSProperties,

    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)',
      borderRadius: '24px',
      border: '1px solid rgba(139, 92, 246, 0.3)',
      color: '#8b5cf6',
      fontSize: '13px',
      fontWeight: 600,
      marginBottom: '20px',
    } as React.CSSProperties,

    title: {
      fontSize: isMobile ? '32px' : '48px',
      fontWeight: 800,
      color: 'var(--text)',
      margin: '0 0 16px 0',
      lineHeight: 1.2,
    } as React.CSSProperties,

    subtitle: {
      fontSize: isMobile ? '16px' : '18px',
      color: 'var(--muted)',
      maxWidth: '600px',
      margin: '0 auto 32px',
      lineHeight: 1.6,
    } as React.CSSProperties,

    billingToggle: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '12px',
      padding: '6px',
      background: 'var(--card-bg)',
      borderRadius: '12px',
      border: '1px solid var(--border)',
    } as React.CSSProperties,

    billingOption: (active: boolean) => ({
      padding: '10px 20px',
      borderRadius: '8px',
      border: 'none',
      background: active ? 'var(--primary)' : 'transparent',
      color: active ? 'white' : 'var(--muted)',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }) as React.CSSProperties,

    savingsBadge: {
      padding: '2px 8px',
      background: 'rgba(16, 185, 129, 0.2)',
      color: '#10b981',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 700,
    } as React.CSSProperties,

    grid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: '20px',
      marginBottom: '24px',
    } as React.CSSProperties,

    freeBanner: {
      background: 'var(--card-bg)',
      borderRadius: '16px',
      border: '1px solid var(--border)',
      padding: isMobile ? '20px' : '24px 32px',
      marginBottom: '48px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row' as const,
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? '16px' : '32px',
    } as React.CSSProperties,

    freeBannerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      flex: isMobile ? 'none' : '0 0 auto',
    } as React.CSSProperties,

    freeBannerIcon: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      flexShrink: 0,
    } as React.CSSProperties,

    freeBannerInfo: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '2px',
    } as React.CSSProperties,

    freeBannerTitle: {
      fontSize: '18px',
      fontWeight: 700,
      color: 'var(--text)',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    } as React.CSSProperties,

    freeBannerSubtitle: {
      fontSize: '13px',
      color: 'var(--muted)',
      margin: 0,
    } as React.CSSProperties,

    freeBannerFeatures: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '12px',
      flex: 1,
    } as React.CSSProperties,

    freeBannerFeature: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '12px',
      color: 'var(--text)',
      background: 'var(--bg)',
      padding: '6px 12px',
      borderRadius: '20px',
      border: '1px solid var(--border)',
    } as React.CSSProperties,

    freeBannerButton: {
      padding: '12px 24px',
      borderRadius: '10px',
      border: 'none',
      background: 'var(--bg)',
      color: 'var(--text)',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      whiteSpace: 'nowrap' as const,
      flexShrink: 0,
    } as React.CSSProperties,

    // VIP Section styles
    vipSection: {
      marginBottom: '48px',
    } as React.CSSProperties,

    vipHeader: {
      textAlign: 'center' as const,
      marginBottom: '24px',
    } as React.CSSProperties,

    vipBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(236, 72, 153, 0.15) 100%)',
      borderRadius: '24px',
      border: '1px solid rgba(245, 158, 11, 0.3)',
      color: '#f59e0b',
      fontSize: '13px',
      fontWeight: 600,
      marginBottom: '12px',
    } as React.CSSProperties,

    vipTitle: {
      fontSize: isMobile ? '24px' : '28px',
      fontWeight: 700,
      color: 'var(--text)',
      margin: '0 0 8px 0',
    } as React.CSSProperties,

    vipSubtitle: {
      fontSize: '14px',
      color: 'var(--muted)',
      margin: 0,
    } as React.CSSProperties,

    vipGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '24px',
    } as React.CSSProperties,

    vipCard: (color: string) => ({
      background: 'var(--card-bg)',
      borderRadius: '20px',
      border: `2px solid ${color}30`,
      padding: '28px',
      position: 'relative' as const,
      overflow: 'hidden',
    }) as React.CSSProperties,

    vipCardGlow: (gradient: string) => ({
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: gradient,
    }) as React.CSSProperties,

    vipCardHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px',
      marginBottom: '20px',
    } as React.CSSProperties,

    vipCardIcon: (gradient: string) => ({
      width: '56px',
      height: '56px',
      borderRadius: '16px',
      background: gradient,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      flexShrink: 0,
    }) as React.CSSProperties,

    vipCardInfo: {
      flex: 1,
    } as React.CSSProperties,

    vipCardName: {
      fontSize: '22px',
      fontWeight: 700,
      color: 'var(--text)',
      margin: '0 0 4px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    } as React.CSSProperties,

    vipCardSubtitle: {
      fontSize: '13px',
      color: 'var(--muted)',
      margin: 0,
    } as React.CSSProperties,

    vipCondition: (color: string) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 16px',
      background: `${color}10`,
      borderRadius: '10px',
      marginBottom: '20px',
      fontSize: '13px',
      color: color,
      fontWeight: 500,
    }) as React.CSSProperties,

    vipEquivalent: {
      fontSize: '12px',
      color: 'var(--muted)',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    } as React.CSSProperties,

    vipFeatureList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    } as React.CSSProperties,

    vipFeatureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '8px 0',
      fontSize: '13px',
      color: 'var(--text)',
    } as React.CSSProperties,

    vipCheckIcon: (color: string) => ({
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      background: `${color}15`,
      color: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }) as React.CSSProperties,

    card: (tier: PricingTier) => ({
      background: 'var(--card-bg)',
      borderRadius: '20px',
      border: tier.highlighted ? `2px solid ${tier.color}` : '1px solid var(--border)',
      padding: '28px',
      position: 'relative' as const,
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      transform: tier.highlighted && !isMobile ? 'scale(1.05)' : 'none',
      boxShadow: tier.highlighted ? `0 20px 40px ${tier.color}20` : '0 4px 12px rgba(0,0,0,0.1)',
    }) as React.CSSProperties,

    cardBadge: (tier: PricingTier) => ({
      position: 'absolute' as const,
      top: '16px',
      right: '16px',
      padding: '4px 12px',
      background: tier.gradient,
      color: 'white',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    }) as React.CSSProperties,

    iconWrapper: (tier: PricingTier) => ({
      width: '56px',
      height: '56px',
      borderRadius: '16px',
      background: tier.gradient,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      marginBottom: '20px',
      boxShadow: `0 8px 24px ${tier.color}40`,
    }) as React.CSSProperties,

    tierName: {
      fontSize: '24px',
      fontWeight: 700,
      color: 'var(--text)',
      margin: '0 0 4px 0',
    } as React.CSSProperties,

    tierSubtitle: {
      fontSize: '13px',
      color: 'var(--muted)',
      margin: '0 0 20px 0',
    } as React.CSSProperties,

    priceWrapper: {
      marginBottom: '24px',
    } as React.CSSProperties,

    price: {
      fontSize: '40px',
      fontWeight: 800,
      color: 'var(--text)',
      lineHeight: 1,
    } as React.CSSProperties,

    priceUnit: {
      fontSize: '14px',
      color: 'var(--muted)',
      fontWeight: 500,
      marginLeft: '4px',
    } as React.CSSProperties,

    annualNote: {
      fontSize: '12px',
      color: 'var(--muted)',
      marginTop: '4px',
    } as React.CSSProperties,

    featureList: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 24px 0',
    } as React.CSSProperties,

    featureItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '8px 0',
      fontSize: '13px',
      color: 'var(--text)',
      lineHeight: 1.4,
    } as React.CSSProperties,

    checkIcon: (color: string) => ({
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      background: `${color}15`,
      color: color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: '1px',
    }) as React.CSSProperties,

    ctaButton: (tier: PricingTier) => ({
      width: '100%',
      padding: '14px 24px',
      borderRadius: '12px',
      border: 'none',
      background: tier.highlighted ? tier.gradient : 'var(--bg)',
      color: tier.highlighted ? 'white' : 'var(--text)',
      fontSize: '15px',
      fontWeight: 600,
      cursor: tier.comingSoon ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      opacity: tier.comingSoon ? 0.6 : 1,
    }) as React.CSSProperties,

    comparisonToggle: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '32px',
    } as React.CSSProperties,

    comparisonButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 24px',
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '12px',
      color: 'var(--text)',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    } as React.CSSProperties,

    comparisonTable: {
      background: 'var(--card-bg)',
      borderRadius: '20px',
      border: '1px solid var(--border)',
      overflow: 'hidden',
      marginBottom: '48px',
    } as React.CSSProperties,

    tableHeader: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '200px repeat(5, 1fr)',
      background: 'var(--bg)',
      borderBottom: '1px solid var(--border)',
      padding: '16px',
      position: 'sticky' as const,
      top: 0,
      zIndex: 10,
    } as React.CSSProperties,

    tableHeaderCell: (highlighted?: boolean) => ({
      padding: '8px',
      textAlign: 'center' as const,
      fontSize: '14px',
      fontWeight: highlighted ? 700 : 600,
      color: highlighted ? '#8b5cf6' : 'var(--text)',
    }) as React.CSSProperties,

    tableCategory: {
      background: 'var(--bg)',
      padding: '12px 16px',
      fontSize: '13px',
      fontWeight: 700,
      color: 'var(--muted)',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
      borderTop: '1px solid var(--border)',
    } as React.CSSProperties,

    tableRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '200px repeat(5, 1fr)',
      borderBottom: '1px solid var(--border)',
      padding: isMobile ? '12px 16px' : '0',
    } as React.CSSProperties,

    tableCell: {
      padding: isMobile ? '4px 0' : '12px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: isMobile ? 'space-between' : 'center',
      fontSize: '13px',
      color: 'var(--text)',
    } as React.CSSProperties,

    tableFeatureName: {
      justifyContent: 'flex-start',
      fontWeight: 500,
    } as React.CSSProperties,

    faq: {
      background: 'var(--card-bg)',
      borderRadius: '20px',
      border: '1px solid var(--border)',
      padding: isMobile ? '24px' : '40px',
    } as React.CSSProperties,

    faqTitle: {
      fontSize: '24px',
      fontWeight: 700,
      color: 'var(--text)',
      textAlign: 'center' as const,
      marginBottom: '32px',
    } as React.CSSProperties,

    faqGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '24px',
    } as React.CSSProperties,

    faqItem: {
      padding: '20px',
      background: 'var(--bg)',
      borderRadius: '12px',
      border: '1px solid var(--border)',
    } as React.CSSProperties,

    faqQuestion: {
      fontSize: '15px',
      fontWeight: 600,
      color: 'var(--text)',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    } as React.CSSProperties,

    faqAnswer: {
      fontSize: '13px',
      color: 'var(--muted)',
      lineHeight: 1.6,
    } as React.CSSProperties,
  };

  const renderCheckOrValue = (value: boolean | string) => {
    if (value === true) {
      return (
        <span style={{ color: '#10b981' }}>
          <Icons.Check size={16} />
        </span>
      );
    }
    if (value === false) {
      return (
        <span style={{ color: 'var(--muted)', opacity: 0.4 }}>
          <Icons.X size={16} />
        </span>
      );
    }
    return <span style={{ fontWeight: 500 }}>{value}</span>;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.badge}>
          <Icons.Sparkles size={16} />
          <span>Tarification simple et transparente</span>
        </div>

        <h1 style={styles.title}>
          Choisissez votre plan
        </h1>

        <p style={styles.subtitle}>
          Démarrez gratuitement et évoluez selon vos besoins.
          Tous les plans incluent les mises à jour et les nouvelles fonctionnalités.
        </p>

        {/* Billing Toggle */}
        <div style={styles.billingToggle}>
          <button
            style={styles.billingOption(billingPeriod === 'monthly')}
            onClick={() => setBillingPeriod('monthly')}
          >
            Mensuel
          </button>
          <button
            style={styles.billingOption(billingPeriod === 'annual')}
            onClick={() => setBillingPeriod('annual')}
          >
            Annuel
            <span style={styles.savingsBadge}>-17%</span>
          </button>
        </div>
      </header>

      {/* Pricing Cards - Paid tiers */}
      <div style={styles.grid}>
        {PRICING_TIERS.map((tier) => {
          const IconComponent = Icons[tier.icon] || Icons.Sparkles;
          const savings = getAnnualSavings(tier);

          return (
            <div key={tier.id} style={styles.card(tier)}>
              {tier.badge && (
                <div style={styles.cardBadge(tier)}>{tier.badge}</div>
              )}
              {tier.comingSoon && (
                <div style={styles.cardBadge(tier)}>Bientôt</div>
              )}

              <div style={styles.iconWrapper(tier)}>
                <IconComponent size={28} />
              </div>

              <h2 style={styles.tierName}>{tier.name}</h2>
              <p style={styles.tierSubtitle}>{tier.subtitle}</p>

              <div style={styles.priceWrapper}>
                <span style={styles.price}>{formatPrice(tier)}</span>
                {tier.monthlyPrice > 0 && (
                  <span style={styles.priceUnit}>/mois</span>
                )}
                {billingPeriod === 'annual' && tier.monthlyPrice > 0 && (
                  <p style={styles.annualNote}>
                    Facturé {tier.annualPrice}€/an
                    {savings > 0 && ` (économisez ${savings}%)`}
                  </p>
                )}
              </div>

              <ul style={styles.featureList}>
                {tier.features.map((feature, idx) => (
                  <li key={idx} style={styles.featureItem}>
                    <span style={styles.checkIcon(tier.color)}>
                      <Icons.Check size={12} />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                style={styles.ctaButton(tier)}
                onClick={() => !tier.comingSoon && handleSelectPlan(tier.id)}
                disabled={tier.comingSoon}
              >
                {tier.comingSoon ? (
                  <>
                    <Icons.Clock size={16} />
                    Bientôt disponible
                  </>
                ) : (
                  <>
                    <Icons.ArrowRight size={16} />
                    Choisir ce plan
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* Free Tier Banner */}
      <div style={styles.freeBanner}>
        <div style={styles.freeBannerLeft}>
          <div style={styles.freeBannerIcon}>
            <Icons.Sparkles size={24} />
          </div>
          <div style={styles.freeBannerInfo}>
            <h3 style={styles.freeBannerTitle}>
              {FREE_TIER.name}
              <span style={{
                fontSize: '14px',
                fontWeight: 700,
                color: '#10b981',
              }}>
                Gratuit
              </span>
            </h3>
            <p style={styles.freeBannerSubtitle}>{FREE_TIER.subtitle}</p>
          </div>
        </div>

        <div style={styles.freeBannerFeatures}>
          {FREE_TIER.features.map((feature, idx) => (
            <span key={idx} style={styles.freeBannerFeature}>
              <Icons.Check size={12} style={{ color: '#10b981' }} />
              {feature}
            </span>
          ))}
        </div>

        <button
          style={styles.freeBannerButton}
          onClick={() => handleSelectPlan('free')}
        >
          {isAuthenticated ? (
            <>
              <Icons.Check size={16} />
              Plan actuel
            </>
          ) : (
            <>
              <Icons.User size={16} />
              Commencer gratuitement
            </>
          )}
        </button>
      </div>

      {/* VIP Section for Hosting/Staking Customers */}
      <div style={styles.vipSection}>
        <div style={styles.vipHeader}>
          <div style={styles.vipBadge}>
            <Icons.Crown size={16} />
            <span>Programme Partenaires</span>
          </div>
          <h2 style={styles.vipTitle}>Offres VIP exclusives</h2>
          <p style={styles.vipSubtitle}>
            Vous utilisez nos services d'hébergement FluxNode ou de staking Cronos ?
            Bénéficiez d'avantages exclusifs.
          </p>
        </div>

        <div style={styles.vipGrid}>
          {VIP_TIERS.map((tier) => {
            const IconComponent = Icons[tier.icon] || Icons.Star;

            return (
              <div key={tier.id} style={styles.vipCard(tier.color)}>
                <div style={styles.vipCardGlow(tier.gradient)} />

                <div style={styles.vipCardHeader}>
                  <div style={styles.vipCardIcon(tier.gradient)}>
                    <IconComponent size={28} />
                  </div>
                  <div style={styles.vipCardInfo}>
                    <h3 style={styles.vipCardName}>
                      {tier.name}
                      <span style={{
                        fontSize: '11px',
                        padding: '3px 8px',
                        background: tier.gradient,
                        color: 'white',
                        borderRadius: '12px',
                        fontWeight: 600,
                      }}>
                        OFFERT
                      </span>
                    </h3>
                    <p style={styles.vipCardSubtitle}>{tier.subtitle}</p>
                  </div>
                </div>

                <div style={styles.vipCondition(tier.color)}>
                  <Icons.Zap size={16} />
                  <span>{tier.condition}</span>
                </div>

                <div style={styles.vipEquivalent}>
                  <Icons.ArrowRight size={12} />
                  <span>Équivalent au plan <strong>{tier.equivalentPlan}</strong></span>
                </div>

                <ul style={styles.vipFeatureList}>
                  {tier.features.map((feature, idx) => (
                    <li key={idx} style={styles.vipFeatureItem}>
                      <span style={styles.vipCheckIcon(tier.color)}>
                        <Icons.Check size={12} />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Comparison Toggle */}
      <div style={styles.comparisonToggle}>
        <button
          style={styles.comparisonButton}
          onClick={() => setShowComparison(!showComparison)}
        >
          {showComparison ? <Icons.ChevronUp size={18} /> : <Icons.ChevronDown size={18} />}
          {showComparison ? 'Masquer la comparaison' : 'Comparer toutes les fonctionnalités'}
        </button>
      </div>

      {/* Comparison Table */}
      {showComparison && !isMobile && (
        <div style={styles.comparisonTable}>
          <div style={styles.tableHeader}>
            <div style={{ ...styles.tableHeaderCell(), textAlign: 'left' }}>Fonctionnalité</div>
            <div style={styles.tableHeaderCell()}>Découverte</div>
            <div style={styles.tableHeaderCell()}>Starter</div>
            <div style={styles.tableHeaderCell(true)}>Pro</div>
            <div style={styles.tableHeaderCell()}>Expert</div>
            <div style={styles.tableHeaderCell()}>Entreprise</div>
          </div>

          {FEATURE_COMPARISON.map((category) => (
            <div key={category.category}>
              <div style={styles.tableCategory}>{category.category}</div>
              {category.features.map((feature) => (
                <div key={feature.name} style={styles.tableRow}>
                  <div style={{ ...styles.tableCell, ...styles.tableFeatureName }}>
                    {feature.name}
                  </div>
                  <div style={styles.tableCell}>{renderCheckOrValue(feature.free)}</div>
                  <div style={styles.tableCell}>{renderCheckOrValue(feature.starter)}</div>
                  <div style={{ ...styles.tableCell, background: 'rgba(139, 92, 246, 0.05)' }}>
                    {renderCheckOrValue(feature.pro)}
                  </div>
                  <div style={styles.tableCell}>{renderCheckOrValue(feature.expert)}</div>
                  <div style={styles.tableCell}>{renderCheckOrValue(feature.enterprise)}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* FAQ */}
      <div style={styles.faq}>
        <h2 style={styles.faqTitle}>Questions fréquentes</h2>
        <div style={styles.faqGrid}>
          <div style={styles.faqItem}>
            <div style={styles.faqQuestion}>
              <Icons.Info size={18} style={{ color: 'var(--primary)' }} />
              Puis-je changer de plan à tout moment ?
            </div>
            <p style={styles.faqAnswer}>
              Oui, vous pouvez passer à un plan supérieur ou inférieur à tout moment.
              Le changement sera proratisé sur votre prochaine facture.
            </p>
          </div>

          <div style={styles.faqItem}>
            <div style={styles.faqQuestion}>
              <Icons.Info size={18} style={{ color: 'var(--primary)' }} />
              Comment fonctionne l'essai gratuit ?
            </div>
            <p style={styles.faqAnswer}>
              Le plan Découverte est gratuit pour toujours avec les fonctionnalités de base.
              Vous pouvez l'utiliser aussi longtemps que vous le souhaitez.
            </p>
          </div>

          <div style={styles.faqItem}>
            <div style={styles.faqQuestion}>
              <Icons.Info size={18} style={{ color: 'var(--primary)' }} />
              Quels moyens de paiement acceptez-vous ?
            </div>
            <p style={styles.faqAnswer}>
              Nous acceptons les cartes bancaires (Visa, Mastercard, American Express)
              et les paiements en crypto (BTC, ETH, USDC).
            </p>
          </div>

          <div style={styles.faqItem}>
            <div style={styles.faqQuestion}>
              <Icons.Info size={18} style={{ color: 'var(--primary)' }} />
              Mes données sont-elles sécurisées ?
            </div>
            <p style={styles.faqAnswer}>
              Absolument. Nous utilisons un chiffrement de bout en bout et ne stockons
              jamais vos clés privées. Vos données restent les vôtres.
            </p>
          </div>

          <div style={styles.faqItem}>
            <div style={styles.faqQuestion}>
              <Icons.Info size={18} style={{ color: 'var(--primary)' }} />
              Comment annuler mon abonnement ?
            </div>
            <p style={styles.faqAnswer}>
              Vous pouvez annuler à tout moment depuis les paramètres de votre compte.
              Votre accès reste actif jusqu'à la fin de la période payée.
            </p>
          </div>

          <div style={styles.faqItem}>
            <div style={styles.faqQuestion}>
              <Icons.Info size={18} style={{ color: 'var(--primary)' }} />
              Proposez-vous des réductions pour les étudiants ?
            </div>
            <p style={styles.faqAnswer}>
              Oui ! Contactez-nous avec votre justificatif étudiant pour obtenir
              50% de réduction sur tous les plans payants.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
